import { inject, Injectable } from '@angular/core'
import { OrderStateServiceV2 } from '../store/state/order/orderv2/order-state.service'
import { OrderStateServiceWrapper } from '../store/state/order/order.state.wrapper.service'
import { SamlCustomerResponse, SamlResponse } from '../models/order/saml/saml.model'
import { PreOrderDetails, StartOrderDetails } from '../models/OrderDetails'
import * as Sentry from "@sentry/angular";
import { OrderSetupData } from '../models/order/workflow/order-setup.model'
import { OrderSetupStateService } from '../store/state/order/orderv2/workflows/order-setup/order-setup-state.service'
import { StepStatusType } from '../models/order/order.model'
import { ServiceInformationCollectionStateService } from '../store/state/order/orderv2/workflows/service-info-collection/service-info-collection-state.service'
import { ServiceInfoCollectionData } from '../models/order/workflow/service-info-collection.model'
import { AuthService } from './auth.service'
import { LoginFlowStateService } from '../store/state/login-flow/login-flow.state.service'
import { UserStateService } from '../store/state/user/user.state.service'
import { LoginResp, UserWelcomeResponse } from '../models/Login'
import { AnalyticsService } from './analytics/analytics.service'
import { AppStateService } from '../store/state/app/app.state.service'
import { SelectItem } from 'primeng/api';
import { DeviceService } from './device.service'
import { of, tap } from 'rxjs'

@Injectable({
    providedIn: 'root'
})
export class SamlService {
    orderStateServiceV2 = inject(OrderStateServiceV2)
    orderStateService = inject(OrderStateServiceWrapper)
    orderSetupStateService = inject(OrderSetupStateService)
    authService = inject(AuthService)
    loginFlowStateService = inject(LoginFlowStateService)
    userStateService = inject(UserStateService)
    analyticsService = inject(AnalyticsService);
    appStateService = inject(AppStateService);
    deviceService = inject(DeviceService);
    serviceInformationCollectionStateService = inject(ServiceInformationCollectionStateService)
    processSamlResponse(samlResponse: SamlResponse) {
        return of(samlResponse).pipe(tap(() => {
            if (samlResponse && samlResponse.ApiResponses) {
                if (samlResponse.ApiResponses.Login) {
                    this.processLogin(samlResponse.ApiResponses.Login);
                }

                if (samlResponse.ApiResponses.Welcome) {
                    this.processWelcome(samlResponse.ApiResponses.Welcome);
                }
                if (samlResponse.ApiResponses.PreOrder) {
                    this.processPreOrder(samlResponse.ApiResponses.PreOrder);
                }

                if (samlResponse.ApiResponses.StartOrder) {
                    this.processStartOrder(samlResponse.ApiResponses.StartOrder);
                }

                this.processOrderSetup(samlResponse);

                if (samlResponse.ApiResponses.Customer) {
                    this.processServiceInformationCollection(samlResponse.ApiResponses.Customer);
                }
            }
            return samlResponse;
        }));
    }


    private processLogin(resp: LoginResp) {
        resp.UserDetails.SamlAuthenticated = true;
        const sessionId = this.appStateService.getSession()?.SessionID
        const authorized = !(resp.LoginFlow.Do2FaCheck || resp.LoginFlow.Do2FaSetup || resp.LoginFlow.DoForcedPasswordReset || resp.LoginFlow.DoSecurityQandA)
        this.deviceService.initDevice(resp.LoginFlow.LocationServicesOptional ?? false);
        this.loginFlowStateService.setLoginFlow(resp.LoginFlow)
        this.userStateService.setUser(resp.UserDetails)
        this.analyticsService.setUser(sessionId, resp);
        this.authService.setSentryUser(authorized)
    }

    private processWelcome(resp: UserWelcomeResponse) {
        this.userStateService.setUserWelcome(resp)
        this.userStateService.setAuthorized(true)
    }

    private processPreOrder(preOrder: PreOrderDetails) {
        this.orderStateService.setPreOrder(preOrder);
        this.orderStateServiceV2.initOrder(preOrder.OrderNumber)
        this.orderStateServiceV2.updateOrder({ agentId: preOrder.AgentId });
        Sentry.setTag('Order', preOrder.OrderNumber);
    }

    private processStartOrder(startOrder: StartOrderDetails) {
        this.orderStateService.setStartOrder(startOrder);
    }

    private processOrderSetup(samlResponse: SamlResponse) {
        const salesPersonId = samlResponse.ApiResponses.StartOrder.SalesPersons.length > 0 ?
            samlResponse.ApiResponses.StartOrder.SalesPersons[0].UserId.toString() : '';

        const account = samlResponse.ApiResponses.StartOrder.Accounts.length > 0 ?
            samlResponse.ApiResponses.StartOrder.Accounts[0].DealerCode : '';
        const orderSetupData: OrderSetupData = {
            orderOptions: {
                orderType: samlResponse.ApiResponses.PreOrder.OrderType,
                interactionType: samlResponse.ApiResponses.PreOrder.InteractionType,
                location: samlResponse.ApiResponses.PreOrder.LocationType,
            },
            salesInformation: {
                account: account,
                salesPerson: salesPersonId,
                selectedDealerCode: account,
                campaign: samlResponse.ApiResponses.StartOrder.Campaigns?.[0]?.Id?.toString() ?? '0',
            },
            referenceNumbers: [],
            status: StepStatusType.InProgress
        }
        this.orderSetupStateService.updateData(orderSetupData);
        this.orderSetupStateService.markStepComplete();
    }

    private mapBusinessSegment(businessSegment: string | null): string | null {
        const option = BUSINESS_SEGMENT_OPTIONS.find(opt => opt.label.toLowerCase() === businessSegment?.toLowerCase());
        return option ? option.value : businessSegment;
    }

    private mapBusinessType(businessType: string | null): string | null {
        const option = BUSINESS_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === businessType?.toLowerCase());
        return option ? option.value : businessType;
    }

    private mapAccountType(accountType: string | null): string | null {
        const option = ACCOUNT_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === accountType?.toLowerCase());
        return option ? option.value : accountType;
    }

    private mapEvo(evo: string | null): string | null {
        const option = EVO_OPTIONS.find(opt => opt.label.toLowerCase() === evo?.toLowerCase());
        return option ? option.value : evo;
    }

    private mapBusinessInternetProvider(provider: string | null): string | null {
        const option = BUSINESS_INTERNET_PROVIDER_OPTIONS.find(opt => opt.label.toLowerCase() === provider?.toLowerCase());
        return option ? option.value : provider;
    }

    private mapBusinessInternetSpeed(speed: string | null): string | null {
        const option = BUSINESS_INTERNET_SPEED_OPTIONS.find(opt => opt.label.toLowerCase() === speed?.toLowerCase());
        return option ? option.value : speed;
    }

    private processServiceInformationCollection(customerResponse: SamlCustomerResponse) {
        const serviceInfoData: ServiceInfoCollectionData = {
            businessInformation: {
                businessCanContact: 'Yes', //TODO
                businessName: customerResponse.BusinessName,
                phone: customerResponse.InstallPhone,
                contactName: customerResponse.CustFullName,
                contactPhone: customerResponse.AltPhone,
                firstName: customerResponse.CustFirstName,
                lastName: customerResponse.CustLastName,
                businessShareInternetProvider: true, //TODO
                businessIsExistingCustomer: false,
                businessInternetProvider: this.mapBusinessInternetProvider(customerResponse.BusinessInternetProvider),
                businessInternetSpeed: this.mapBusinessInternetSpeed(customerResponse.BusinessInternetSpeed),
                email: customerResponse.Email
            },
            billingAddress: {
                serviceAddress: customerResponse.BillingAddress1,
                city: customerResponse.BillingCity,
                state: customerResponse.BillingState,
                zipCode: customerResponse.BillingZip,
                unitType: customerResponse.CustUnitType,
                unitNumber: customerResponse.CustUnitNumber,
                addressManuallyVerified: false,
            },
            customerInformation: {
                firstName: customerResponse.CustFirstName,
                lastName: customerResponse.CustLastName,
                email: customerResponse.Email,
                phone: customerResponse.InstallPhone,
                alternatePhone: customerResponse.AltPhone,
                canContact: false,
                isExistingCustomer: 'No', //TODO
                isEmailVerifiedByCustomer: false,
                isPhoneVerifiedByCustomer: false,
                govtApproved: null,
                customerSelected: false,
                selectedCustomer: null,    //TODO
                customerCreditChecked: false
            },
            serviceAvailability: {
                serviceAddress: customerResponse.CustAddress1,
                city: customerResponse.CustCity,
                state: customerResponse.CustState,
                zipCode: customerResponse.CustZip,
                unitType: customerResponse.CustUnitType,
                unitNumber: customerResponse.CustUnitNumber,
                addressManuallyVerified: false
            },
            commercialAccount: {
                businessSegment: this.mapBusinessSegment(customerResponse.BusinessSegment),
                businessType: this.mapBusinessType(customerResponse.BusinessType),
                accountType: this.mapAccountType(customerResponse.AccountType),
                evo: this.mapEvo(customerResponse.BusinessEvo),
                fco: customerResponse.BusinessFco,
                canContactCommercial: false
            },
            businessInformationSMB: {
                businessNameSMB: '',
                businessTypeSMB: '',
                businessTaxIdSMB: '',
                businessTaxIdSMBRaw: ''
            },
            cpni: {},
            status: StepStatusType.InProgress
        }
        this.serviceInformationCollectionStateService.updateData(serviceInfoData);
        this.serviceInformationCollectionStateService.markStepComplete();
    }
}

export const BUSINESS_SEGMENT_OPTIONS: SelectItem[] = [
    { label: 'Business Viewing', value: 'Business' },
    { label: 'Private Viewing', value: 'Private' },
    { label: 'Public Viewing', value: 'Public' },
];

export const BUSINESS_INTERNET_PROVIDER_OPTIONS: SelectItem[] = [
    { label: 'AT&T', value: 'AT&T' },
    { label: 'Allo Fiber', value: 'AlloFiber'},
    { label: 'Consolidated', value: 'Consolidated' },
    { label: 'Charter', value: 'Charter' },
    { label: 'Earthlink', value: 'Earthlink' },
    { label: 'Frontier', value: 'Frontier' },
    { label: 'Windstream/Kinetic', value: 'Windstream/Kinetic' },
    { label: 'Ziply', value: 'Ziply' },
    { label: 'HughesNet', value: 'HughesNet' },
    { label: 'Altice', value: 'Altice' },
    { label: 'WOW!', value: 'WOW!' },
    { label: 'Brightspeed', value: 'Brightspeed' },
    { label: 'Alta fiber', value: 'Alta fiber' },
    { label: 'Viasat', value: 'Viasat' },
    { label: 'Hawaiian Telecom', value: 'Hawaiian Telecom' },
    { label: 'Century Link/Lumen/Quantum Fiber', value: 'Century Link/Lumen/Quantum Fiber' },
    { label: 'Metronet', value: 'Metronet' },
    { label: 'Starlink', value: 'Starlink' },
    { label: 'Other', value: 'Other' },
];

export const BUSINESS_INTERNET_SPEED_OPTIONS: SelectItem[] = [
    { label: '0-10 mbps (download)', value: '0-10' },
    { label: '11-100 mbps (download)', value: '11-100' },
    { label: '101-300 mbps (download)', value: '101-300' },
    { label: '301-500 mbps (download)', value: '301-500' },
    { label: '501-700 mbps (download)', value: '501-700' },
    { label: '701-900 mbps (download)', value: '701-900' },
    { label: '1+ GB (download)', value: '1GB' },
];

export const BUSINESS_TYPE_OPTIONS_SMB: SelectItem[] = [
    { label: 'Corporation', value: 'corp' },
    { label: 'Limited Liability Company (LLC)', value: 'llc' },
    { label: 'Sole Proprietorship', value: 'sole-prop' }
];

export const BUSINESS_TYPE_OPTIONS = [
    { label: 'Offshore Oil Rig', value: 'Offshore Oil Rig', accountType: ['OIL'] },
    { label: 'Onshore Oil Drill', value: 'Onshore Oil Drill', accountType: ['OIL'] },
    { label: 'State Government', value: 'State Government', accountType: ['FIR'] },
    { label: 'Federal Government', value: 'Federal Government', accountType: ['FIR'] },
    { label: 'Local Government', value: 'Local Government', accountType: ['FIR'] },
    { label: 'Home Based Business', value: 'Home Based Business', accountType: ['POL'] },
    { label: 'Manufacturing/Industrial', value: 'Manufacturing/Industrial', accountType: ['POL', 'FIR'] },
    { label: 'Professional Office', value: 'Professional Office', accountType: ['POL', 'FIR'] },
    { label: 'Bars and Lounges', value: 'Bars and Lounges', accountType: ['BAR', 'GOV'] },
    { label: 'Restaurants', value: 'Restaurants', accountType: ['BAR', 'GOV'] },
    { label: 'Quick Serve Restaurants', value: 'Quick Serve Restaurants', accountType: ['BAR', 'GOV'] },
    { label: 'Casino', value: 'Casino', accountType: ['BAR', 'GOV'] },
    { label: 'Entertainment/Recreation', value: 'Entertainment/Recreation', accountType: ['BAR', 'GOV'] },
    { label: 'Automotive Sales and Services', value: 'Automotive Sales and Services', accountType: ['HOS'] },
    { label: 'Beauty and Personal Care', value: 'Beauty and Personal Care', accountType: ['HOS'] },
    { label: 'Retail', value: 'Retail', accountType: ['HOS'] },
    { label: 'Religious Organizations', value: 'Religious Organizations', accountType: ['HOS'] },
    { label: 'Membership Club', value: 'Membership Club', accountType: ['HOS'] },
    { label: 'Medical Office', value: 'Medical Office', accountType: ['HOS'] },
    { label: 'Health and Fitness', value: 'Health and Fitness', accountType: ['HOS'] },
    { label: 'Banks and Financial Institutions', value: 'Banks and Financial Institutions', accountType: ['HOS'] },
    { label: 'Personal Services', value: 'Personal Services', accountType: ['HOS'] }
];

export const ACCOUNT_TYPE_OPTIONS = [
    { label: 'HOS - Retail and Waiting Rooms (General)', value: 'HOS', businessSegments: ['Business'] },
    { label: 'BVN - Retail and Waiting Rooms (National Accounts)', value: 'BVN', businessSegments: [] },
    { label: 'CNP - Commercial Non-Profit (National Accounts)', value: 'CNP', businessSegments: [] },
    { label: 'POL - Private Offices (General)', value: 'POL', businessSegments: ['Private'] },
    { label: 'FIR - Private Office (Government)', value: 'FIR', businessSegments: ['Private'] },
    { label: 'NPV - National Private Viewing (National Accounts)', value: 'NPV', businessSegments: [] },
    { label: 'OIL - Oil Rigs/Platforms', value: 'OIL', businessSegments: ['Private'] },
    { label: 'BAR - Bars and Restaurants (General)', value: 'BAR', businessSegments: ['Public'] },
    { label: 'BLK - Bars & Restaurants (National Accounts)', value: 'BLK', businessSegments: [] },
    { label: 'GOV - Bars & Restaurants (Government)', value: 'GOV', businessSegments: ['Public'] }
];

export const EVO_OPTIONS: SelectItem[] = [
    { label: 'None', value: 'None' },
    { label: '1-50', value: '1-50' },
    { label: '51-100', value: '51-100' },
    { label: '101-150', value: '101-150' },
    { label: '151-200', value: '151-200' },
    { label: '201-350', value: '201-350' },
    { label: '351-500', value: '351-500' },
    { label: '501-750', value: '501-750' },
    { label: '751-1000', value: '751-1000' },
    { label: '1001-1500', value: '1001-1500' },
    { label: '1501-2000', value: '1501-2000' },
    { label: '2001-5000', value: '2001-5000' },
    { label: '5001-10000', value: '5001-10000' },
    { label: '10001-99999', value: '10001-99999' }
];
